import { useEffect, useMemo, useState } from "react";
import { normalizeString } from "@rebuild/shared";

interface PermutationCatSelSelectProps {
  options: string[];
  /** Normalized-permutation -> cat/sel badge (e.g. "DRY | DERA"). */
  badgeByPermutation: Map<string, string>;
  value?: string;
  onSelect: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
}

const CatSelBadge: React.FC<{ badge: string }> = ({ badge }) => (
  <span className="shrink-0 rounded-full bg-stone-100 px-2 py-0.5 font-mono text-xs text-stone-600">{badge}</span>
);

/**
 * Select for picking an exact line item permutation. Mirrors {@link FilterableSelect} but shows each permutation's
 * xactimate cat/sel badge both on the dropdown rows and next to the field once a permutation is selected.
 */
export const PermutationCatSelSelect: React.FC<PermutationCatSelSelectProps> = ({
  options,
  badgeByPermutation,
  value,
  onSelect,
  placeholder = "select permutation",
  disabled,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setSearchTerm(value ?? "");
  }, [value]);

  const filteredOptions = useMemo(
    () => (searchTerm ? options.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase())) : options),
    [options, searchTerm],
  );

  const selectedBadge = value ? badgeByPermutation.get(normalizeString(value)) : undefined;

  const handleSelect = (option: string) => {
    onSelect(option);
    setIsOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1">
        <input
          type="text"
          disabled={disabled}
          value={searchTerm || ""}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => {
            setSearchTerm("");
            setIsOpen(true);
          }}
          onBlur={() => {
            setSearchTerm(value ?? "");
            setIsOpen(false);
          }}
          className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={placeholder}
        />
        {(value || searchTerm) && (
          <button
            disabled={disabled}
            onClick={() => onSelect(undefined)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        )}
        {isOpen && filteredOptions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {filteredOptions.map((option) => {
              const badge = badgeByPermutation.get(normalizeString(option));
              return (
                <button
                  key={option}
                  onMouseDown={() => handleSelect(option)}
                  className="w-full flex items-start justify-between gap-2 text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                >
                  <span className="text-left">{option}</span>
                  {badge ? <CatSelBadge badge={badge} /> : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {selectedBadge ? <CatSelBadge badge={selectedBadge} /> : null}
    </div>
  );
};
